import { api } from "../api/client";
import { parseFrontMatter, stringifyFrontMatter } from "./front-matter";
import { draftBranchForBatch } from "../../shared/batch";
import {
  canPublish,
  canTransition,
  normalizeDocStatus,
  SANITY_TARGET_TYPES,
  type SanityTargetType,
} from "../../shared/types";

/** Read a doc from the batch's working branch (where freshly staged edits live),
 *  falling back to the base branch. Throws if neither has it. */
async function readDocFromBatchOrBase(
  path: string,
  batch: string,
): Promise<{ content: string; sha: string }> {
  const ref = draftBranchForBatch(batch) ?? undefined;
  try {
    return await api.git.file(path, ref);
  } catch {
    if (!ref) throw new Error("could not read from git");
    return api.git.file(path);
  }
}

/**
 * Promote a pre-release doc from draft → review by committing the status change
 * onto the batch's working branch. Reads the latest version from that branch
 * (falling back to the base branch). No-op if the doc is already past draft.
 */
export async function promoteToReview(
  path: string,
  batch: string,
): Promise<void> {
  const file = await readDocFromBatchOrBase(path, batch);

  const { data: fm, content: body } = parseFrontMatter(file.content);
  const status = normalizeDocStatus(fm.status);
  if (status !== "draft") return;
  if (!canTransition("draft", "review")) return;

  const content = stringifyFrontMatter({ ...fm, status: "review" }, body);
  await api.git.commit({
    path,
    content,
    message: `review: ${(fm.title as string) ?? path}`,
    sha: file.sha,
    action: "status",
    batch,
  });
}

export interface BulkPublishResult {
  path: string;
  title: string;
  status: "done" | "skipped" | "failed";
  detail?: string;
  /** Paths staged onto the batch (for optimistic FAB updates). */
  staged: string[];
}

/**
 * Headless equivalent of the review page's "generate & publish" flow, for
 * bulk-publishing from the dashboard. For one doc it: reads the version from
 * the default branch, renders the PDF, then publishes to Sanity and stages
 * both the rendered PDF and the git status→published change onto `batch`'s
 * draft branch so they land on the single floating-PR batch.
 *
 * Only docs whose git status is review (canPublish) are acted on; anything
 * else is reported as skipped. The doc is read from the batch's working branch
 * (where a freshly promoted review status lives) before publishing.
 */
export async function publishDocToBatch(
  path: string,
  batch: string,
): Promise<BulkPublishResult> {
  const staged: string[] = [];
  let title = path;

  let file: { content: string; sha: string };
  try {
    file = await readDocFromBatchOrBase(path, batch);
  } catch {
    return {
      path,
      title,
      status: "failed",
      detail: "could not read from git",
      staged,
    };
  }

  const { data: fm, content: body } = parseFrontMatter(file.content);
  title = (fm.title as string) ?? path;
  const status = normalizeDocStatus(fm.status);
  if (!canPublish(status)) {
    return {
      path,
      title,
      status: "skipped",
      detail: `not in review (status: ${status})`,
      staged,
    };
  }

  const version = (fm.version as string) ?? "";
  const docType = (fm.doc_type as string) ?? "methodology";
  const familySlug =
    (fm.family as string) ?? path.split("/").slice(-2)[0] ?? path;
  const slug = ((fm.slug as string) ?? "").trim();
  const effectiveDate = (fm.effective_date as string) ?? "";
  const supersedes = (fm.supersedes as string) ?? "";
  const ticker = (fm.ticker as string) ?? "";
  const materialChange = String(fm.material_change) === "true";
  const targetType: SanityTargetType = SANITY_TARGET_TYPES.includes(
    docType as SanityTargetType,
  )
    ? (docType as SanityTargetType)
    : "methodology";

  // 1. Render the PDF.
  let blob: Blob;
  try {
    const { response } = await api.convert.pdf({
      template: targetType,
      title,
      version,
      content: body,
      metadata: {
        title,
        version,
        effectiveDate,
        status,
        ...(materialChange ? { materialChange: "true" } : {}),
      },
    });
    if (!response.ok) {
      return {
        path,
        title,
        status: "failed",
        detail: "PDF render failed",
        staged,
      };
    }
    blob = await response.blob();
  } catch {
    return {
      path,
      title,
      status: "failed",
      detail: "PDF render failed",
      staged,
    };
  }

  // 2. Stage the rendered PDF onto the batch (best-effort).
  const pdfPath = path.replace(/\.md$/, ".pdf");
  try {
    const pdfRes = await api.git.commitPdf({
      path: pdfPath,
      message: `publish: ${title} ${version} (PDF)`,
      pdf: blob,
      batch,
    });
    const pdfData = (await pdfRes.json().catch(() => null)) as {
      staged?: boolean;
    } | null;
    if (pdfData?.staged) staged.push(pdfPath);
  } catch {
    // Non-fatal — Sanity is the primary publish target.
  }

  // 3. Publish to Sanity + stage the git status→published change onto the batch.
  const pdfFile = new File([blob], `${familySlug}-${version}.pdf`, {
    type: "application/pdf",
  });
  let res: Response;
  try {
    res = await api.sanity.publish({
      familySlug,
      versionString: version,
      pdfFile,
      title,
      docType,
      targetType,
      ...(ticker.trim() ? { ticker: ticker.trim().toUpperCase() } : {}),
      ...(slug ? { slug } : {}),
      effectiveDate,
      status,
      ...(supersedes ? { supersedesVersion: supersedes } : {}),
      ...(materialChange
        ? {
            materialChange: true,
            noticeDate: new Date().toISOString().split("T")[0],
          }
        : {}),
      batch,
    });
  } catch {
    return {
      path,
      title,
      status: "failed",
      detail: "Sanity publish failed",
      staged,
    };
  }

  if (!res.ok) {
    let detail = "Sanity publish failed";
    try {
      const err = (await res.json()) as { error?: string };
      if (err.error) detail = err.error;
    } catch {
      /* keep default */
    }
    return { path, title, status: "failed", detail, staged };
  }

  const data = (await res.json().catch(() => ({}))) as {
    publishStaged?: boolean;
    publishPath?: string;
  };
  if (data.publishStaged && data.publishPath) staged.push(data.publishPath);

  return { path, title, status: "done", staged };
}
